import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import type {
  DashboardProductUpdate,
  WithPagination,
} from "../../../types/types";
import { ErrorPlaceholder } from "../../../components/Error";
import { Fragment, useEffect } from "react";
import {
  DashboardUpdatesHeader,
  DashboardUpdatesWrapper,
  GotoUpdate,
  KeyValueWrapper,
  UpdateEmptyState,
  UpdateKeyValuePairs,
  UpdateUpperSection,
  UpdateWrapper,
} from "./dashboard.util";
import {
  H3DarkText,
  H4,
  SoftHeader,
  SoftHeaderMediumDarkText,
} from "../../../components/Typography/Typography";
import { LoaderContainer } from "../../public/pages.public.util";
import { Loader } from "../../../components/Loader/Loader";
import {
  StatusLeft,
  getProductStatusColor,
  getProductStatusText,
} from "../../../components/Status/Status";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { useRoutePath } from "../../../util/Routing";
import type { AxiosError } from "axios";

export type DashboardChangeRequestUpdate = DashboardProductUpdate;

export const DashboardChangeRequestUpdates = ({
  data,
  error,
  setSize,
  size,
}: {
  data: DashboardChangeRequestUpdate[];
  error: AxiosError<any>;
  setSize: (
    size: number | ((size: number) => number)
  ) => Promise<
    WithPagination<{ data: DashboardChangeRequestUpdate[] }>[] | undefined
  >;
  size: number;
}) => {
  const { t } = useTranslation();
  const { ref: inViewRef, inView } = useInView({ triggerOnce: true });

  const isLoadingInitialData = !data && !error;

  const isLoadingMore =
    isLoadingInitialData ||
    (size > 0 && data && typeof data[size - 1] === "undefined");

  useEffect(() => {
    if (inView) {
      setSize((size) => size + 1);
    }
  }, [inView, setSize]);

  if (error) {
    return (
      <ErrorPlaceholder
        message={t(
          "There was an error from fetching change request updates. Please try again later"
        )}
      />
    );
  }

  return (
    <DashboardUpdatesWrapper>
      <DashboardUpdatesHeader>
        <H4>{t("Change Requests")}</H4>
      </DashboardUpdatesHeader>
      {data.length > 0 ? (
        data.map((update, index) => (
          // a product can have many change requests, so key on the request id
          <Fragment key={update.update_data.change_request_id}>
            <ChangeRequestUpdateComponent updateData={update} />
            {isLoadingMore && index === data.length - 1 && (
              <LoaderContainer>
                <Loader isLoading={!!isLoadingMore} omitContainer={true} />
              </LoaderContainer>
            )}
            {index === data.length - 4 && (
              <div key={update.type_id + "observer"} ref={inViewRef} />
            )}
          </Fragment>
        ))
      ) : (
        <UpdateEmptyState>
          <H3DarkText>{t("No recent change requests")}</H3DarkText>
        </UpdateEmptyState>
      )}
    </DashboardUpdatesWrapper>
  );
};

function ChangeRequestUpdateComponent({
  updateData,
}: {
  updateData: DashboardChangeRequestUpdate;
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const { adminPath } = useRoutePath();
  const { update_data, modified_by } = updateData;

  const headerTitle = update_data?.request_title ?? t("Change Requested");
  const displayModifiedBy = update_data.requesting_user ?? modified_by;

  const keyValuePairs = [
    { label: t("ID"), value: update_data.product_id ?? "--" },
    { label: t("Name"), value: update_data.name ?? "--" },
    {
      label: t("Request Details"),
      value: update_data.request_details ?? "--",
    },
  ];

  const time = moment(
    new Date(update_data.submitted_at ?? updateData.last_modified_date)
  ).fromNow();

  const handleUpdateClick = () => {
    history.push(
      `${adminPath}/pim/products/${updateData.type_id}?tabKey=change_request&changeRequestId=${update_data.change_request_id}`
    );
  };

  return (
    <UpdateWrapper onClick={handleUpdateClick}>
      <UpdateUpperSection>
        <KeyValueWrapper>
          <SoftHeaderMediumDarkText>{headerTitle}</SoftHeaderMediumDarkText>
          <SoftHeader>{`${displayModifiedBy?.name ?? ""} | ${
            displayModifiedBy?.role ?? ""
          }`}</SoftHeader>
        </KeyValueWrapper>
        <KeyValueWrapper style={{ alignItems: "flex-end" }}>
          <StatusLeft
            color={getProductStatusColor(update_data.status)}
            text={getProductStatusText(update_data.status, t)}
            textStyle={{ fontSize: "13px" }}
            productStatusType="product"
          />
          <SoftHeader>{time}</SoftHeader>
        </KeyValueWrapper>
      </UpdateUpperSection>
      <UpdateKeyValuePairs data={keyValuePairs} />
      <GotoUpdate />
    </UpdateWrapper>
  );
}
